<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Breadcrumbs from './BreadCrumbs.vue'
import Footer from './FooterSection.vue'
import { ContentLibraryService } from '../services/ContentLibraryService'
import { handleErrors } from '../utils/errorHandler'
import { useSessionStorageStore } from '../stores/SessionStorage'
import { normalizeContentLibraryEmbedUrl } from '../utils/contentLibraryHelpers'

const RESIZE_MESSAGE_TYPE = 'bcs-vibe-tools:resize'
const DEFAULT_IFRAME_HEIGHT = 600

const customNavigation = inject('customNavigation')
const contentLibraryService = ContentLibraryService()
const sessionStorageStore = useSessionStorageStore()

const loading = ref(true)
const item = ref(null)
const iframeHeight = ref(DEFAULT_IFRAME_HEIGHT)
const iframeRef = ref(null)

const itemId = computed(() => {
  const fromQuery = customNavigation?.value?.internalQuery?.itemId
  if (fromQuery) {
    return fromQuery
  }

  try {
    const stored = sessionStorageStore.getEvent('contentLibraryEmbeddedToolInternalQuery')
    return stored?.itemId || ''
  } catch {
    return ''
  }
})

const pageTitle = computed(() => {
  return item.value?.libraryFileName || item.value?.documentDisplayName || 'Embedded Tool'
})

const embedUrl = computed(() => normalizeContentLibraryEmbedUrl(item.value?.embedUrl))

const allowedMessageOrigin = computed(() => {
  if (!embedUrl.value) {
    return ''
  }

  try {
    return new URL(embedUrl.value).origin
  } catch {
    return ''
  }
})

const goBack = () => {
  const navigation = customNavigation?.value
  if (!navigation) {
    return
  }

  if (navigation.backStack?.length) {
    navigation.goBack()
    return
  }

  navigation.navigate({
    newPage: navigation.routes.contentLibrary.name
  })
}

const loadItem = async () => {
  if (!itemId.value) {
    item.value = null
    loading.value = false
    return
  }

  loading.value = true
  try {
    item.value = await contentLibraryService.getItem(itemId.value)
  } catch (error) {
    handleErrors('ContentLibraryEmbeddedTool.loadItem', error)
    item.value = null
  } finally {
    loading.value = false
  }
}

const handleEmbedMessage = (event) => {
  if (!allowedMessageOrigin.value || event.origin !== allowedMessageOrigin.value) {
    return
  }

  if (event.source !== iframeRef.value?.contentWindow) {
    return
  }

  if (event.data?.type !== RESIZE_MESSAGE_TYPE) {
    return
  }

  const nextHeight = Number(event.data.height)
  if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
    return
  }

  iframeHeight.value = Math.ceil(nextHeight)
}

onMounted(async () => {
  window.addEventListener('message', handleEmbedMessage)
  await loadItem()
})

onBeforeUnmount(() => {
  window.removeEventListener('message', handleEmbedMessage)
})

watch(itemId, async (newItemId, previousItemId) => {
  if (newItemId && newItemId !== previousItemId) {
    iframeHeight.value = DEFAULT_IFRAME_HEIGHT
    await loadItem()
  }
})
</script>

<template>
  <div class="content-library-embedded-tool-page bcs-page-padding">
    <div class="mt-4">
      <Breadcrumbs />
      <button
        type="button"
        class="btn btn-link p-0 mb-3 text-14 text-primary text-decoration-none"
        @click="goBack"
      >
        <i class="bi bi-chevron-left me-1"></i>
        <strong>Back to BCS Content Library</strong>
      </button>
      <p class="content-library-embedded-tool-page__title text-28">{{ pageTitle }}</p>
    </div>

    <div v-if="loading" class="content-library-embedded-tool-page__status text-14">
      Loading tool...
    </div>
    <div v-else-if="!embedUrl" class="content-library-embedded-tool-page__status text-14">
      This content item does not have an embed URL configured.
    </div>
    <iframe
      v-else
      ref="iframeRef"
      :src="embedUrl"
      class="content-library-embedded-tool-page__frame"
      :style="{ height: `${iframeHeight}px` }"
      :title="pageTitle"
    ></iframe>
  </div>
  <Footer />
</template>

<style scoped lang="scss">
.content-library-embedded-tool-page__title {
  margin-top: 17px;
  margin-left: 9px;
}

.content-library-embedded-tool-page__status {
  margin-top: 24px;
}

.content-library-embedded-tool-page__frame {
  border: 0;
  display: block;
  margin-top: 24px;
  overflow: hidden;
  width: 100%;
}
</style>
